import React, { useContext, useEffect, useRef, useState } from "react";
import PhoneInput, { CountryData } from "react-phone-input-2";
import { PHONE_NUMBER, PhoneValidationRegexKeys } from "../../../../constants/global";
import { hasXSSCharacters, isEmail, isFullNameValid } from "../../../../helpers/Utils";
import { ChooseRecipientsStepModel, IChooseRecipientsStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseRecipientsStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { GiftCardSendTypeEnum } from "../../../../models/enums/GiftCardSendTypeEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";

interface IChooseRecipientsStepProps extends IBuyGiftCardStepProps {
    stepModel: IChooseRecipientsStepModel;
    isAvailable: boolean;
    cardsQuantity: number;

    handleSendIndividuallyChange(sendIndividually: boolean): void;
    handleSubmit(stepModel: IChooseRecipientsStepModel): void;
}

const ChooseRecipientsStep = (props: IChooseRecipientsStepProps) => {
    const { labels } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    const phoneNumberMaxLength = PHONE_NUMBER.VALIDATIONS.MAXLENGTH;
    let phoneInputDropdowns = document.querySelectorAll('.flag-dropdown') as NodeListOf<HTMLElement>;
    let phoneNumberErrorMessages = document.querySelectorAll('.invalid-number-message') as NodeListOf<HTMLElement>;

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IChooseRecipientsStepModel>({
        sendIndividually: props.stepModel?.sendIndividually ?? true,
        sendBy: props.stepModel?.sendBy ?? GiftCardSendTypeEnum.BY_EMAIL,
        recipients: props.stepModel?.recipients
    });
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [isLoading, setIsLoading] = useState(true);
    const [recipientErrors, setRecipientErrors] = useState(Array<{
        fullName: string,
        email: string,
    }>);
    const [recipientFieldsCount, setRecipientFieldsCount] = useState(props.cardsQuantity ?? 0);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        updateRecipientDetailsErrors();

        setStepModel({
            ...stepModel,
            recipients: props.stepModel.recipients?.slice(0, props.cardsQuantity)
        });
    }, []);

    useEffect(() => {
        handlePhoneNumberInputStyles();
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.CHOOSE_RECIPIENTS;

        if (props.isAvailable && !stepModel.sendIndividually) {
            setRecipientFieldsCount(1);
        }
    }, [props.activeStep]);

    useEffect(() => {
        setRecipientFieldsCount(props.cardsQuantity);
    }, [props.cardsQuantity]);

    useEffect(() => {
        updateRecipientDetailsErrors();
        setStepModel(props.stepModel);
    }, [props.stepModel]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (props.isAvailable) {
            if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.CHOOSE_RECIPIENTS) {
                props.handleSetActiveStep(BuyGiftCardStateEnum.CHOOSE_RECIPIENTS);
            }
        }
    };

    const handleSendIndividuallyChange = (sendIndividually: boolean): void => {
        props.handleSendIndividuallyChange(sendIndividually);

        if (sendIndividually) {
            setRecipientFieldsCount(props.cardsQuantity);
        } else {
            setRecipientFieldsCount(1);
        }
    };

    const handleSendByChange = (sendBy: GiftCardSendTypeEnum): void => {
        setStepModel({
            ...stepModel,
            sendBy: sendBy
        });
    };

    const handleRecipientDetailsChange = (event: React.ChangeEvent<HTMLInputElement>, index: number): void => {
        const { name, value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
            return;
        }

        let errMsg = '';

        if (name === 'fullName') {
            if (!value.trim()) {
                errMsg = 'Full name is required';
            } else if (!isFullNameValid(value.trim())) {
                errMsg = 'Invalid full name';
            }
        } else if (name === 'email') {
            if (!value.trim()) {
                errMsg = 'Email is required';
            } else if (!isEmail(value)) {
                errMsg = 'Invalid email';
            }
        }

        setRecipientErrors((prevModel) =>
            prevModel.map((recErr, i) =>
                i === index ? { ...recErr, [name]: errMsg } : recErr
            )
        );

        setStepModel((prevModel) => ({
            ...prevModel,
            recipients: prevModel.recipients.map((recipient, i) =>
                i === index ? { ...recipient, [name]: value } : recipient
            )
        }));
    };

    const handlePhoneNumberInputStyles = (): void => {
        phoneInputDropdowns = document.querySelectorAll('.flag-dropdown') as NodeListOf<HTMLElement>;
        phoneNumberErrorMessages = document.querySelectorAll('.invalid-number-message') as NodeListOf<HTMLElement>;

        if (phoneInputDropdowns && phoneInputDropdowns.length > 0) {
            phoneInputDropdowns.forEach(dropdown => {
                dropdown.style.borderColor = '#6b7280';
                dropdown.style.borderRightColor = '#cacaca';
            });
        }

        if (phoneNumberErrorMessages && phoneNumberErrorMessages.length > 0) {
            phoneNumberErrorMessages.forEach(errorMessage => {
                errorMessage.style.left = '0';
                errorMessage.style.top = '42px';
                errorMessage.classList.add("react-input-error-message");
            });
        }
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const updateRecipientDetailsErrors = (): void => {
        const recipientErrors = [];

        for (let i = 0; i < props.cardsQuantity; i++) {
            recipientErrors.push({
                fullName: '',
                email: '',
            });
        }

        setRecipientErrors(recipientErrors);
    };

    const isValid = (): boolean => {
        return ChooseRecipientsStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="recipients box">
            <input
                type="checkbox"
                name="choose_recipients_toggle"
                id="choose_recipients_toggle"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-02.png" alt="icon 02" />
                    </i>
                    <div className="head-options">
                        <span>{labels.get("GiftCard.App.V2.ChooseRecipients.Title")}</span>

                        {(props.activeStep === BuyGiftCardStateEnum.CHOOSE_RECIPIENTS && props.cardsQuantity > 1) && (
                            <div>
                                <ul>
                                    <li>
                                        <input
                                            type="radio"
                                            name="rb_individually"
                                            id="rb_individually"
                                            checked={stepModel.sendIndividually}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleSendIndividuallyChange(true);
                                                }
                                            }}
                                        />
                                        <label htmlFor="rb_individually">
                                            {labels.get("GiftCard.App.V2.ChooseRecipients.Button.SendIndividually")}
                                        </label>
                                    </li>
                                    <li>
                                        <input
                                            type="radio"
                                            name="rb_shared"
                                            id="rb_shared"
                                            checked={!stepModel.sendIndividually}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleSendIndividuallyChange(false);
                                                }
                                            }}
                                        />
                                        <label htmlFor="rb_shared">
                                            {labels.get("GiftCard.App.V2.ChooseRecipients.Button.SendToOne")}
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                    <i className="box-arrow"
                        style={{ cursor: props.isAvailable ? "pointer" : "default" }}
                        onClick={() => {
                            if (props.isAvailable) {
                                checkboxRef.current.checked = !checkboxRef.current.checked;
                                handleExpandBoxCheck();
                            }
                        }}
                    ></i>
                </h4>

                {props.activeStep === BuyGiftCardStateEnum.CHOOSE_RECIPIENTS && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content">
                                <div className="how-like-send">
                                    <p>{labels.get("GiftCard.App.V2.ChooseRecipients.Label.HowToSend")}</p>
                                    <ul>
                                        <li>
                                            <input
                                                type="radio"
                                                name="rb_sendby_email"
                                                id="rb_sendby_email"
                                                checked={stepModel.sendBy === GiftCardSendTypeEnum.BY_EMAIL}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSendByChange(GiftCardSendTypeEnum.BY_EMAIL);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="rb_sendby_email">
                                                {labels.get("GiftCard.App.V2.ChooseRecipients.Button.SendByEmail")}
                                            </label>
                                        </li>
                                        <li>
                                            <input
                                                type="radio"
                                                name="rb_sendby_sms"
                                                id="rb_sendby_sms"
                                                checked={stepModel.sendBy === GiftCardSendTypeEnum.BY_SMS}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSendByChange(GiftCardSendTypeEnum.BY_SMS);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="rb_sendby_sms">
                                                {labels.get("GiftCard.App.V2.ChooseRecipients.Button.SendBySms")}
                                            </label>
                                        </li>
                                        <li>
                                            <input
                                                type="radio"
                                                name="rb_sendby_pdf"
                                                id="rb_sendby_pdf"
                                                checked={stepModel.sendBy === GiftCardSendTypeEnum.BY_PDF}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSendByChange(GiftCardSendTypeEnum.BY_PDF);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="rb_sendby_pdf">
                                                {labels.get("GiftCard.App.V2.ChooseRecipients.Button.SendByPdf")}
                                            </label>
                                        </li>
                                    </ul>
                                </div>
                                <div className="input-content">
                                    <ul>
                                        {Array.from({ length: recipientFieldsCount }).map((_, index) => (
                                            <React.Fragment key={`recipient_${index}`}>
                                                {stepModel.sendBy === GiftCardSendTypeEnum.BY_EMAIL && (
                                                    <>
                                                        <li className="fadeIn">
                                                            <input
                                                                type="text"
                                                                id={`recipient_fullname_${index + 1}`}
                                                                name="fullName"
                                                                placeholder={labels.get("GiftCard.App.V2.ChooseRecipients.Placeholder.FullName")}
                                                                className={recipientErrors[index]?.fullName ? "react-input-error" : ""}
                                                                value={stepModel.recipients[index]?.fullName ?? ""}
                                                                maxLength={100}
                                                                onChange={e => handleRecipientDetailsChange(e, index)}
                                                            />
                                                            <input
                                                                type="text"
                                                                id={`recipient_email_${index + 1}`}
                                                                name="email"
                                                                placeholder={labels.get("GiftCard.App.V2.ChooseRecipients.Placeholder.Email")}
                                                                className={recipientErrors[index]?.email ? "react-input-error" : ""}
                                                                value={stepModel.recipients[index]?.email ?? ""}
                                                                onChange={e => handleRecipientDetailsChange(e, index)}
                                                            />
                                                        </li>
                                                    </>
                                                )}

                                                {stepModel.sendBy === GiftCardSendTypeEnum.BY_SMS && (
                                                    <>
                                                        <li className="fadeIn">
                                                            <input
                                                                type="text"
                                                                id={`recipient_fullname_${index + 1}`}
                                                                name="fullName"
                                                                placeholder={labels.get("GiftCard.App.V2.ChooseRecipients.Placeholder.FullName")}
                                                                className={recipientErrors[index]?.fullName ? "react-input-error" : ""}
                                                                value={stepModel.recipients[index]?.fullName ?? ""}
                                                                maxLength={100}
                                                                onChange={e => handleRecipientDetailsChange(e, index)}
                                                            />
                                                            <PhoneInput
                                                                placeholder={labels.get("GiftCard.App.V2.ChooseRecipients.Placeholder.Phone")}
                                                                country={PHONE_NUMBER.DEFAULT_COUNTRY.toLowerCase()}
                                                                onlyCountries={Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase())}
                                                                countryCodeEditable={false}
                                                                inputClass='react-phone-input'
                                                                defaultErrorMessage={labels.get("GiftCard.App.V2.ChooseRecipients.Error.Phone")}
                                                                enableLongNumbers={phoneNumberMaxLength}
                                                                value={stepModel.recipients[index]?.phone ?? ""}
                                                                onChange={(phone) => {
                                                                    if (phone.length > phoneNumberMaxLength) {
                                                                        phone = phone.slice(0, phoneNumberMaxLength);
                                                                    }

                                                                    setStepModel((prevModel) => ({
                                                                        ...prevModel,
                                                                        recipients: prevModel.recipients.map((recipient, i) =>
                                                                            i === index ? { ...recipient, phone: `+${phone}` } : recipient
                                                                        )
                                                                    }));
                                                                }}
                                                                isValid={(inputNumber, data: CountryData) => {
                                                                    if (!data || !data.iso2) {
                                                                        return false;
                                                                    }

                                                                    const allowedCountriesAbbr = Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase());
                                                                    if (allowedCountriesAbbr.includes(data.iso2.toLowerCase())) {
                                                                        const regexKey = data.iso2.toUpperCase() as PhoneValidationRegexKeys;
                                                                        const regex = PHONE_NUMBER.VALIDATIONS.REGEX[regexKey] as RegExp;

                                                                        if (regex) {
                                                                            return regex.test(stepModel.recipients[index]?.phone);
                                                                        }
                                                                    }

                                                                    return false;
                                                                }}
                                                            />
                                                        </li>
                                                    </>
                                                )}

                                                {stepModel.sendBy === GiftCardSendTypeEnum.BY_PDF && (
                                                    <>
                                                        <li className="full-width fadeIn">
                                                            <input
                                                                type="text"
                                                                id={`recipient_fullname_${index + 1}`}
                                                                name="fullName"
                                                                placeholder={labels.get("GiftCard.App.V2.ChooseRecipients.Placeholder.FullName")}
                                                                className={recipientErrors[index]?.fullName ? "react-input-error" : ""}
                                                                value={stepModel.recipients[index]?.fullName ?? ""}
                                                                maxLength={100}
                                                                onChange={e => handleRecipientDetailsChange(e, index)}
                                                            />
                                                        </li>
                                                    </>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </ul>
                                </div>
                                <button
                                    className="btn btn__full_width btn__primary large-fs btn__large"
                                    onClick={handleSaveAndContinue}
                                    disabled={!isModelValid}
                                >
                                    {labels.get("GiftCard.App.V2.ChooseRecipients.Button.Submit")}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChooseRecipientsStep;